// Desktop command palette (lg+ only; ConsoleShell mounts it on desktop
// exclusively). ⌘K / Ctrl+K toggles, Esc or backdrop closes.
//
// Purely additive chrome: actions navigate to existing routes, and party
// jump reuses the parties list endpoint the Parties screen already calls
// (client-side filtering only). No new endpoints, no mutations.
//
// NAMING + RELEVANCE (2026-08-12 operator pass): party rows render through
// the shared <Name/> with `partyName()` + `partySubtitle()`, so a palette hit
// carries the same `Kind · Standing · State` line as the Parties list. The
// list is capped at the 8 most relevant (shared comparator); a short query
// keeps tiers 0–2, and from two characters on the search reaches stale rows
// too — you can always type your way to anything.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Star, User, Wallet } from 'lucide-react';
import { api, type PartySummary } from '../lib/api';
import {
  partyName, partySubtitle, needsNameReview, compareRelevance, partitionByRelevance,
} from '../lib/naming';
import { readWords } from '../lib/errors';
import { navTitle, paletteRoutes } from '../lib/routes';
import { uiV2 } from '../lib/ui-v2';
import { isOwnAccount, partiesOnly, treasuryHref } from '../lib/ownAccounts';
import { Name } from './Name';
import { rosterCut } from '../lib/roster-cut';

// THE PALETTE LISTS THE ONE LABEL TABLE (sweep item 71, 14 Sept 2026): every
// row is a lib/routes.ts entry under its canonical screen name — the word the
// screen's own header and the top bar use — so a search for "Convert" or
// "Settle later" finds the screen by the name it wears. Rare flows (Legacy
// history, Names, Inter-entity) live here and nowhere else on the chrome.
// With the rebuild's batch 1 on, the closed desk's inter-entity door leaves the palette with its route (App.tsx).
const ACTIONS = paletteRoutes().filter((r) => !(uiV2(1) && r.path === '/inter-entity'));

const MAX_PARTIES = 8;
/** Desk accounts a typed search offers, under their own label (operator, 21 Oct 2026: they are not parties). */
const MAX_ACCOUNTS = 5;

/** WHAT THE PALETTE OFFERS FOR A TYPED NEEDLE. Customers and merchants under "Jump to party"; the desk's own accounts under
 *  their own label, only when something is typed, and they open on Treasury (operator, 21 Oct 2026: "in party, I am still
 *  seeing treasury accounts, which it should't display"). A pure function so the rule can be tested without typing. */
export function paletteHits<T extends PartySummary>(everyone: readonly T[], needle: string): { parties: T[]; accounts: T[]; moreCount: number } {
  // Match the normalized name too — the operator sees "Grypto", not the
  // stored "GRYPTO TG", so typing what is on screen has to find the row.
  const matches = (p: T) =>
    partyName(p).toLowerCase().includes(needle) ||
    p.display_name.toLowerCase().includes(needle) ||
    p.slug.toLowerCase().includes(needle);
  const all = partiesOnly(everyone);
  const hits = needle ? all.filter(matches) : all;
  // Two characters of intent unlock the stale tier; below that the palette
  // stays on the live book. Favorites are tier 0, so they float for free.
  const { shown } = partitionByRelevance(hits, { maxTier: needle.length >= 2 ? 3 : 2 });
  const ordered = [...shown].sort(compareRelevance);
  const accounts = needle
    ? everyone
        .filter((p) => isOwnAccount(p) && !!p.treasury_slug && (matches(p) || p.treasury_slug!.toLowerCase().includes(needle)))
        .sort((a, b) => partyName(a).localeCompare(partyName(b)))
        .slice(0, MAX_ACCOUNTS)
    : [];
  return { parties: ordered.slice(0, MAX_PARTIES), accounts, moreCount: Math.max(0, ordered.length - MAX_PARTIES) };
}

export function CommandPalette({ open, onOpenChange }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Same endpoint the Parties screen hits; lives under the same 'parties'
  // root key, so favorite toggles elsewhere invalidate this list too.
  // THE WHOLE ROSTER, as the Parties screen asks for it: the endpoint's default page is 100 and the desk has 134 active parties, so
  // a filter that runs HERE, on what came back, could never find the last 34 ("No matching parties." over a party who exists).
  const partiesQuery = useQuery({
    queryKey: ['parties', 'palette', 'dubaiotc'],
    queryFn: () => api.listParties({ entity: 'dubaiotc', pageSize: 500 }),
    enabled: open,
    staleTime: 60_000,
  });

  // ⌘K / Ctrl+K toggles from anywhere; Escape closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      } else if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  // Fresh query + selection every time the palette opens.
  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
    }
  }, [open]);

  const needle = q.trim().toLowerCase();
  // A roster that ran past its page lists the first of them only: nothing says "none" over it (rosterIsCut, whatever is typed), and a
  // typed search says it looked in the first of them (cut). The page's rows include the desk's own accounts, which are not drawn here.
  const rosterIsCut = rosterCut(partiesQuery.data);
  const cut = needle !== '' && rosterIsCut;

  const actions = useMemo(
    () => (needle ? ACTIONS.filter((a) => navTitle(a).toLowerCase().includes(needle)) : ACTIONS),
    [needle],
  );

  const { parties, accounts, moreCount } = useMemo(() => paletteHits(partiesQuery.data?.parties ?? [], needle), [partiesQuery.data, needle]);

  const go = (to: string) => {
    onOpenChange(false);
    nav(to);
  };

  // Flat run-list across both sections for arrow-key navigation.
  const runners: Array<() => void> = [
    ...actions.map((a) => () => go(a.path)),
    ...parties.map((p) => () => go(`/parties/${encodeURIComponent(p.slug)}?entity=dubaiotc`)),
    ...accounts.map((p) => () => go(treasuryHref(p.treasury_slug))),
  ];
  const runAt = (i: number) => runners[i]?.();
  const activeIdx = runners.length ? Math.min(active, runners.length - 1) : -1;

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-idx="${activeIdx}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  const onInputKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, runners.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      runAt(activeIdx);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 pt-[12vh]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div className="w-[560px] max-w-[92vw] overflow-hidden rounded-xl border border-stroke bg-panel shadow-2xl">
        <input
          autoFocus
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
          }}
          onKeyDown={onInputKeyDown}
          placeholder="Type a command or party name…"
          className="w-full border-b border-stroke-soft bg-transparent px-4 py-3.5 text-field lg:text-body text-tg-text placeholder:text-brand-muted focus:outline-none"
        />
        <div ref={listRef} className="max-h-[380px] overflow-y-auto p-1.5">
          {actions.length > 0 && <SectionLabel>Actions</SectionLabel>}
          {actions.map((a, i) => (
            <Row key={a.path} idx={i} active={i === activeIdx} onHover={setActive} onRun={() => runAt(i)}>
              {a.icon && <a.icon size={14} className="shrink-0 text-brand-muted" />}
              <span>{navTitle(a)}</span>
            </Row>
          ))}

          <SectionLabel>Jump to party</SectionLabel>
          {parties.map((p, i) => {
            const idx = actions.length + i;
            return (
              <Row key={p.slug} idx={idx} active={idx === activeIdx} onHover={setActive} onRun={() => runAt(idx)}>
                {p.is_favorite
                  ? <Star size={13} className="shrink-0 fill-current text-tg-accent" />
                  : <User size={13} className="shrink-0 text-brand-muted" />}
                <span className="min-w-0 flex-1">
                  <Name
                    primary={partyName(p)}
                    secondary={partySubtitle(p)}
                    review={needsNameReview(p.display_name)}
                  />
                </span>
              </Row>
            );
          })}
          {accounts.length > 0 && <SectionLabel>On Treasury</SectionLabel>}
          {accounts.map((p, i) => {
            const idx = actions.length + parties.length + i;
            return (
              <Row key={p.slug} idx={idx} active={idx === activeIdx} onHover={setActive} onRun={() => runAt(idx)}>
                <Wallet size={13} className="shrink-0 text-brand-muted" />
                <span className="min-w-0 flex-1">
                  <Name primary={partyName(p)} secondary="Treasury account" />
                </span>
              </Row>
            );
          })}
          {partiesQuery.isLoading && <Hint>Loading parties…</Hint>}
          {partiesQuery.isError && <Hint>Couldn&rsquo;t read parties — {readWords(partiesQuery.error)}</Hint>}
          {partiesQuery.data && parties.length === 0 && accounts.length === 0 && !rosterIsCut && (
            <Hint>{needle ? 'No matching parties.' : 'No parties yet.'}</Hint>
          )}
          {/* PAST THE PAGE (`more`, even at 500) the palette searched the first of them and cannot say "no such party": it says what it
              searched, and where the rest is. */}
          {rosterIsCut && (cut || (parties.length === 0 && accounts.length === 0)) && (
            <Hint>
              {parties.length === 0 && accounts.length === 0 ? (needle ? 'Not in' : 'Nobody in') : 'Searched'} the first {partiesQuery.data!.parties.length} — Parties has the rest.
            </Hint>
          )}
          {moreCount > 0 && (
            <Hint>{moreCount} more — keep typing to narrow it down.</Hint>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pb-1 pt-2 text-label font-medium uppercase tracking-[0.08em] text-brand-muted">
      {children}
    </div>
  );
}

function Hint({ children }: { children: ReactNode }) {
  return <div className="px-3 py-2 text-body text-tg-hint">{children}</div>;
}

function Row({ idx, active, onHover, onRun, children }: {
  idx: number;
  active: boolean;
  onHover: (idx: number) => void;
  onRun: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-idx={idx}
      onMouseEnter={() => onHover(idx)}
      onClick={onRun}
      className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-body text-tg-text transition-colors ${active ? 'bg-panel-2' : ''}`}
    >
      {children}
    </button>
  );
}
